import { END, type EventChannel, eventChannel, type SagaIterator } from 'redux-saga';
import { all, call, cancelled, put, take, takeEvery } from 'redux-saga/effects';
import type { ActionType } from 'typesafe-actions';
import handleError from '../common/utils/handle-error.js';
import uploadFile, { type UploadEvent } from '../common/utils/upload-file.js';
import { showMessageSaga } from '../messages/saga.js';
import { replaceAsset, uploadAsset, uploadAssetProgress, uploadAssets } from './actions.js';
import { getReplacementUploadUrl, getUploadUrls, type UploadFile } from './api.js';

// Convert upload events to generator
export function uploadChannel(upload: UploadFile): EventChannel<UploadEvent> {
  return eventChannel((emitter) => {
    return uploadFile(upload.file, upload.uploadUrl, (event) => {
      emitter(event);
      if (event.done) {
        emitter(END);
      }
    });
  });
}

export function* uploadAssetSaga(upload: UploadFile): SagaIterator {
  const { filename } = upload.asset;

  const chan = yield call(uploadChannel, upload);
  try {
    while (true) {
      const { progress, done, error }: UploadEvent = yield take(chan);
      if (done) {
        if (error) {
          yield put(uploadAsset.rejected(error, { filename }));
        } else {
          yield put(uploadAsset.fulfilled(undefined, { filename }));
        }

        break;
      }
      yield put(uploadAssetProgress({ filename, progress }));
    }
  } finally {
    if (yield cancelled()) {
      chan.close();
    }
  }
}

export function* uploadAssetsSaga(action: ActionType<typeof uploadAssets.requested>): SagaIterator {
  try {
    yield put(uploadAssets.pending(undefined, action.meta));
    yield all(action.payload.map((file) => put(uploadAsset.pending(undefined, { filename: file.name }))));

    const uploads: UploadFile[] = yield call(getUploadUrls, action.payload);
    yield all(uploads.map((asset) => call(uploadAssetSaga, asset)));
    yield put(uploadAssets.fulfilled(uploads, action.meta));
    const { callback } = action.meta;
    if (callback) {
      yield call(callback, uploads);
    }
  } catch (error) {
    const errorAction = handleError(error);
    if (errorAction) {
      yield put(errorAction);
    }

    yield all(action.payload.map((file) => put(uploadAsset.rejected(error.message, { filename: file.name }))));
    yield put(uploadAssets.rejected(error.message, action.meta));
  }
}

export function* replaceAssetSaga({ meta, payload }: ActionType<typeof replaceAsset.requested>): SagaIterator {
  const { assetId, assetVersion } = meta;
  yield put(replaceAsset.pending(undefined, meta));
  yield put(uploadAsset.pending(undefined, { filename: payload.name }));

  try {
    const upload = yield call(getReplacementUploadUrl, assetId, assetVersion, payload);
    yield call(uploadAssetSaga, upload);
    yield put(replaceAsset.fulfilled(payload, meta));
  } catch (error) {
    const errorAction = handleError(error);
    if (errorAction) {
      yield put(errorAction);
    }

    yield put(replaceAsset.rejected(error.message, meta));
  }
}

export const showUploadErrorSaga = showMessageSaga(
  'error',
  (action: ActionType<typeof uploadAsset.rejected> | ActionType<typeof replaceAsset.rejected>) => action.payload,
  5000
);

export default function* root(): SagaIterator {
  yield all([
    takeEvery(replaceAsset.REQUESTED, replaceAssetSaga),
    takeEvery(uploadAssets.REQUESTED, uploadAssetsSaga),
    takeEvery([replaceAsset.REJECTED, uploadAsset.REJECTED], showUploadErrorSaga)
  ]);
}
